import { Autocomplete, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import CurrencyInput from "@/components/atoms/CurrencyInput";
import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import { applyDocumentMask } from "@/helpers/document-helpers";
import useGetCustomerById from "@/hooks/requests/customer/use-get-customer-by-id";
import useGetBankAccounts from "@/hooks/requests/t2Pay/use-get-bank-accounts";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import usePagination from "@/hooks/use-pagination";
import { AccountBalance, OperationType, PaymentPayload } from "@/interfaces/talk2pay.iterfaces";
import { PaymentFormMessages } from "@/messages/platforms/talk2PayHome/payment-form.messages";

import { ACCOUNT_TYPE_OPTIONS, BANKS_OPTIONS } from "./constants";
import { buildPaymentFormSchema, buildPaymentInitialData } from "./form-data";

type Props = {
  messages: PaymentFormMessages;
  onSubmit: (values: PaymentPayload) => void;
  onCancel: () => void;
  balance: AccountBalance | null;
};

const PaymentForm = ({ messages, onSubmit, onCancel, balance }: Props) => {
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const customerId = useGetCustomerId();
  const _id = Number(customerId) || 0;
  const getCustomer = useGetCustomerById({ _id });
  const { data: customer, isLoading: isLoadingCustomer, error: errorCustomer } = getCustomer;
  const getAccounts = useGetBankAccounts({ pageNumber, pageSize });
  const { data: accounts, isLoading: isLoadingAccounts, error: errorAccounts } = getAccounts;

  const isLoading = isLoadingCustomer || isLoadingAccounts;
  const hasError = Boolean(errorCustomer || errorAccounts);

  const operationTypeOptions = ACCOUNT_TYPE_OPTIONS();

  return (
    <Suspense isSuspended={isLoading} fallback={<CircularProgress />}>
      {!hasError && (
        <Formik
          initialValues={buildPaymentInitialData(customer)}
          validationSchema={buildPaymentFormSchema(messages, balance)}
          onSubmit={onSubmit}
        >
          {({ values, errors, handleChange, setFieldValue, submitForm }) => (
            <MDBox p={3}>
              <MDTypography variant="h5" fontWeight="bold">
                {messages.modalTitle}
              </MDTypography>

              <MDBox mt={3}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Autocomplete
                      fullWidth
                      options={operationTypeOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      value={
                        operationTypeOptions.find(
                          (option) => option.value === values.operationType
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue("operationType", newValue ? newValue.value : null);
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label={messages.operationTypeLabel}
                          variant="standard"
                          error={!!errors.operationType}
                          helperText={errors.operationType}
                        />
                      )}
                    />
                  </Grid>

                  {values.operationType === OperationType.TED && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Autocomplete
                        fullWidth
                        options={BANKS_OPTIONS(accounts?.items)}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={
                          BANKS_OPTIONS(accounts?.items).find(
                            (option) => option.value === values.beneficiaryAccountId
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          setFieldValue("beneficiaryAccountId", newValue ? newValue.value : null);
                        }}
                        loadingText={messages.loadingText}
                        noOptionsText={messages.noOptionsItems}
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            label={messages.beneficiaryAccountLabel}
                            variant="standard"
                            error={!!errors.beneficiaryAccountId}
                            helperText={errors.beneficiaryAccountId}
                            disabled={isLoadingAccounts}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoadingAccounts && (
                                    <CircularProgress color="inherit" size={20} />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            inputProps={{ ...params.inputProps, maxLength: 250 }}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  {values.operationType === OperationType.PIX && (
                    <>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <InputMaskWrapper
                          mask={applyDocumentMask(values.pixAddressKey || "")}
                          value={values.pixAddressKey as string}
                          onChange={handleChange}
                          disabled
                          required
                          variant="standard"
                          name="pixAddressKey"
                          label={messages.pixAddressKeyLabel}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.pixAddressKey}
                          helperText={errors.pixAddressKey}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <CurrencyInput
                      name="amount"
                      label={messages.amountLabel}
                      value={values.amount}
                      onChange={(e) => {
                        setFieldValue("amount", e.target.value);
                      }}
                      fullWidth
                      error={!!errors.amount}
                      helperText={errors.amount}
                    />
                  </Grid>

                  {values.operationType === OperationType.PIX && (
                    <Grid size={{ xs: 12, sm: 12 }}>
                      <FormField
                        externalLabel
                        name="description"
                        multiline
                        rows={4}
                        label={messages.descriptionLabel}
                        value={values.description}
                        onChange={handleChange}
                        fullWidth
                        inputProps={{ maxLength: 250 }}
                      />
                    </Grid>
                  )}
                </Grid>

                <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
                  <MDButton variant="gradient" color="light" onClick={onCancel}>
                    {messages.cancelButtonLabel}
                  </MDButton>
                  <MDButton variant="gradient" color="dark" onClick={submitForm}>
                    {messages.confirmButtonLabel}
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </Formik>
      )}

      {errorCustomer && (
        <AlertBlock
          title={messages.errorTitleLoadingCustomer}
          description={errorCustomer.message}
          alt="Customer Error"
        />
      )}

      {errorAccounts && (
        <AlertBlock
          title={messages.errorTitleLoadingAccounts}
          description={errorAccounts.message}
          alt="Bank Accounts Error"
        />
      )}
    </Suspense>
  );
};

export default PaymentForm;
